import { barSeconds } from './clock';

export const CROSSFADE_SECONDS = barSeconds();

const CURVE_POINTS_PER_SECOND = 200;

// The gain follows a quarter sine of the presence: two layers crossing keep the total power constant.
export interface Fade {
  readonly from: number;
  readonly to: number;
  readonly start: number;
  readonly seconds: number;
}

export interface Fader {
  // From wherever the layer is now; returns when the fade ends.
  to(presence: number): number;
}

export function presenceGain(presence: number): number {
  return Math.sin((presence * Math.PI) / 2);
}

export function restingAt(presence: number): Fade {
  return { from: presence, to: presence, start: 0, seconds: 0 };
}

export function presenceAt(fade: Fade, time: number): number {
  if (time >= fade.start + fade.seconds) {
    return fade.to;
  }
  if (time <= fade.start) {
    return fade.from;
  }
  return fade.from + ((fade.to - fade.from) * (time - fade.start)) / fade.seconds;
}

export function fadeTo(
  current: Fade,
  presence: number,
  at: number,
  crossfade = CROSSFADE_SECONDS,
): Fade {
  const from = presenceAt(current, at);
  return { from, to: presence, start: at, seconds: Math.abs(presence - from) * crossfade };
}

export function fadeCurve(fade: Fade): Float32Array<ArrayBuffer> {
  const points = Math.max(2, Math.ceil(fade.seconds * CURVE_POINTS_PER_SECOND));
  const curve = new Float32Array(points);
  for (let index = 0; index < points; index += 1) {
    curve[index] = presenceGain(fade.from + ((fade.to - fade.from) * index) / (points - 1));
  }
  return curve;
}

export function createFader(
  param: AudioParam,
  context: BaseAudioContext,
  presence: number,
  crossfade: () => number = () => CROSSFADE_SECONDS,
): Fader {
  let fade = restingAt(presence);
  param.value = presenceGain(presence);
  return {
    to(target) {
      const now = context.currentTime;
      fade = fadeTo(fade, target, now, crossfade());
      param.cancelScheduledValues(now);
      if (fade.seconds > 0) {
        param.setValueCurveAtTime(fadeCurve(fade), now, fade.seconds);
      } else {
        param.setValueAtTime(presenceGain(target), now);
      }
      return now + fade.seconds;
    },
  };
}
